import { useState } from 'react';
import { Download } from 'lucide-react';
import {
  RECOMMENDATION_EXPORT_DATASETS,
  type ExportFormat,
  type RecommendationExportDataset,
} from '@ederest/contracts';
import { download } from '../../shared/api/client.js';
import { Button } from '../../shared/ui/Button.js';
import '../../shared/ui/export-menu.css';

const DATASET_LABELS: Record<RecommendationExportDataset, string> = {
  'platform-changes': 'Recommandations plateforme',
  decisions: 'Décisions prises',
};

/**
 * The site changes handed to the client and the trace of every decision
 * (functional §5.6), in CSV or JSON. Every download is one audited call.
 */
export function RecommendationExportMenu({ projectId }: { projectId: string | undefined }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = async (dataset: RecommendationExportDataset, format: ExportFormat) => {
    if (!projectId) return;
    setBusy(`${dataset}:${format}`);
    setError(null);
    try {
      await download(
        `/composition/projects/${projectId}/recommendations/exports/${dataset}?format=${format}`,
      );
      setOpen(false);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "L'export a échoué");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="export">
      <Button
        icon={<Download />}
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((v) => !v)}
      >
        Exporter
      </Button>
      {open && (
        <div className="export__menu" role="menu" aria-label="Exporter les recommandations">
          {RECOMMENDATION_EXPORT_DATASETS.map((dataset) => (
            <div key={dataset} className="export__row">
              <span className="export__label">{DATASET_LABELS[dataset]}</span>
              <span className="export__formats">
                {(['csv', 'json'] as const).map((format) => (
                  <button
                    key={format}
                    type="button"
                    role="menuitem"
                    disabled={busy !== null}
                    onClick={() => void run(dataset, format)}
                    aria-label={`${DATASET_LABELS[dataset]} en ${format.toUpperCase()}`}
                  >
                    {busy === `${dataset}:${format}` ? '…' : format.toUpperCase()}
                  </button>
                ))}
              </span>
            </div>
          ))}
          <p className="export__hint">
            Les modifications du site sont remises au client par cet export. Chaque export est journalisé.
          </p>
          {error && (
            <p className="export__error" role="alert">
              {error}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
